import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { formatTopDonorAmount } from '@/lib/landing-top-donors'
import type { RankingPositionOffer } from './ranking-dispute'
import styles from './RankingDisputeDialog.module.css'

type Props = {
  offers: RankingPositionOffer[] | null
  viewerTotalAmount: string | null
  unitPrice: number | string | null
  minimumQuantity: number | null
  maximumQuantity: number | null
  salesOpen: boolean
  onClose: () => void
  onAssume: (offer: RankingPositionOffer) => void
}

const money = (cents: number) => formatTopDonorAmount(cents / 100)

export function RankingDisputeDialog({
  offers,
  viewerTotalAmount,
  unitPrice,
  minimumQuantity,
  maximumQuantity,
  salesOpen,
  onClose,
  onAssume,
}: Props) {
  const dialog = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const element = dialog.current
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    element?.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      element?.close()
      document.body.style.overflow = previousOverflow
      previousFocus?.focus({ preventScroll: true })
    }
  }, [])

  const min = Math.max(1, minimumQuantity ?? 1)

  return createPortal(
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="ranking-dispute-title"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>
            <Icon icon="mdi:trophy-outline" aria-hidden /> SUA DISPUTA COMEÇA AQUI
          </span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Fechar disputa"
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
          <h2 id="ranking-dispute-title">Escolha a posição que quer conquistar.</h2>
          <p>Veja quanto falta e quantos números da sorte você recebe em cada compra.</p>
        </header>

        <div className={styles.summary}>
          <span>
            <Icon icon="mdi:wallet-outline" aria-hidden /> Você já contribuiu{' '}
            <strong>
              {viewerTotalAmount == null ? '—' : formatTopDonorAmount(viewerTotalAmount)}
            </strong>
          </span>
          <span>
            <Icon icon="mdi:ticket-confirmation-outline" aria-hidden /> Cada número{' '}
            <strong>{unitPrice == null ? '—' : formatTopDonorAmount(unitPrice)}</strong>
          </span>
          <span>
            Compra mínima: {min} {min === 1 ? 'número' : 'números'}
            {maximumQuantity ? ` · Máximo: ${maximumQuantity}` : ''}
          </span>
        </div>

        {!offers ? (
          <p className={styles.notice} role="status">
            Não foi possível calcular os valores agora. Atualize a página e tente
            novamente.
          </p>
        ) : (
          <ol
            className={styles.offers}
            aria-label="Valores para disputar as dez posições"
          >
            {offers.map((offer) => (
              <li
                key={offer.rank}
                className={`${styles.offer} ${offer.rank <= 3 ? styles.podium : ''}`}
              >
                <div className={styles.offerHead}>
                  <span className={styles.rank}>{offer.rank}º</span>
                  <div>
                    <strong>
                      {offer.rank === 1 ? 'Topo do ranking' : `${offer.rank}ª posição`}
                    </strong>
                    <small>
                      {offer.occupied && offer.currentAmountCents != null
                        ? `Valor atual: ${money(offer.currentAmountCents)}`
                        : 'Posição livre'}
                    </small>
                  </div>
                </div>
                {offer.status === 'already' ? (
                  <p className={styles.already}>
                    <Icon icon="mdi:check-circle" aria-hidden /> Você já alcançou esta
                    posição
                  </p>
                ) : (
                  <>
                    <p className={styles.gap}>
                      {offer.occupied
                        ? `Faltam pelo menos ${money(offer.differenceCents)} para superar essa posição.`
                        : 'A compra mínima já coloca você na disputa.'}
                    </p>
                    <div className={styles.purchase}>
                      <span>
                        <strong>{money(offer.amountCents)}</strong> nesta compra
                      </span>
                      <span>
                        <Icon icon="mdi:ticket-confirmation" aria-hidden />{' '}
                        {offer.quantity}{' '}
                        {offer.quantity === 1 ? 'número da sorte' : 'números da sorte'}
                      </span>
                    </div>
                    <p className={styles.projected}>
                      Seu total depois do Pix: {money(offer.projectedTotalCents)}
                    </p>
                    {offer.status === 'over-limit' ? (
                      <p className={styles.limit}>
                        Essa quantidade ultrapassa o máximo permitido em uma compra.
                      </p>
                    ) : (
                      <button
                        type="button"
                        className={styles.assume}
                        disabled={!salesOpen}
                        onClick={() => onAssume(offer)}
                      >
                        Assumir {offer.rank}º lugar{' '}
                        <Icon icon="mdi:arrow-right" aria-hidden />
                      </button>
                    )}
                  </>
                )}
              </li>
            ))}
          </ol>
        )}
        <p className={styles.footnote}>
          {salesOpen
            ? 'A posição é uma estimativa do ranking atual. Ela só muda após a confirmação do Pix e pode variar se outras pessoas comprarem antes.'
            : 'Esta campanha não está recebendo compras no momento.'}
        </p>
      </div>
    </dialog>,
    document.body
  )
}
